
<script setup>
import { ref, computed, onMounted, watch, reactive } from 'vue'
import Button from '@/mini/components/Button.vue'
import { useRoute, useRouter } from 'vue-router'
import Sep from '@/mini/components/Sep.vue'
import Space from '@/mini/components/Space.vue'
import Pagination from '@/mini/components/Pagination.vue'
import { jsonServerUrl, BACKEND_ORIGIN } from '@/config/api'
import { useAuthStore } from '@/mini/stores/auth'
import { useCardSearch } from '@/composables/useCardSearch'
import { useMarketPrices } from '@/composables/useMarketPrices'
import { useCardtrader } from '@/composables/useCardtrader'
import { useModalRouting } from '@/composables/useModalRouting'
import { rarityClass } from '@/composables/useRarity'
import { createFilterMode, createPageSizePref, createSortPref } from '@/composables/useFilterMode'
import { useMessage } from '@/mini/composables/useMessage'
import CardBox from '@/components/CardToSellBox.vue'
import CardEditModal from '@/components/CardEditModal.vue'
import { useModalsStore } from '@/stores/modals'
import { useCardActionsStore } from '@/stores/cardActions'
import pinia from '@/stores/pinia'
import { normalizeCard } from '@/api/cardApi'
import Container from '@/mini/components/Container.vue'
import Boxes from '@/mini/components/Boxes.vue'
import Box from '@/mini/components/Box.vue'

const authStore = useAuthStore()
const modalsStore = useModalsStore(pinia)
const cardActionsStore = useCardActionsStore(pinia)
const { showPersistentMessage, updateMessage, dismissMessage } = useMessage()
const cards = ref([])
const { activeModal, modalCard, openModal, closeModal } = useModalRouting()
const { isFetchingAllCards, fetchMarketPricesForCards } = useMarketPrices(authenticatedApiFetch)
const {
  isFetchingBlueprints,
  saveCtPrice,
  saveRealPrice,
  fetchCardtraderDataForCard,
  fetchCardtraderDataForCards,
  refetchExpansionForCard,
  fetchCtMarketPricesForCards,
  createListing: createCardtraderProduct,
  checkListing: checkCardtraderListing,
  deleteListing: deleteCardtraderListing,
  updateListing: updateCardtraderListing,
} = useCardtrader(authenticatedApiFetch)

const expandedCards = ref(new Set())

function toggleDetails(cardId) {
  if (expandedCards.value.has(cardId)) {
    expandedCards.value.delete(cardId)
  } else {
    expandedCards.value.add(cardId)
  }
  expandedCards.value = new Set(expandedCards.value)
}

const allDetailsExpanded = computed(() =>
  pagedCards.value.length > 0 && pagedCards.value.every(card => expandedCards.value.has(card.id))
)

function toggleAllDetails() {
  if (allDetailsExpanded.value) {
    pagedCards.value.forEach(card => expandedCards.value.delete(card.id))
  } else {
    pagedCards.value.forEach(card => expandedCards.value.add(card.id))
  }
  expandedCards.value = new Set(expandedCards.value)
}

const defaultSortColumns = [{ key: 'card_id', dir: 1 }]
const sortPref = createSortPref('optcg.cardList.sortColumns')
const sortColumns = ref(sortPref.getStored([])) // active UI sort only [{ key, dir }]

function resetSorting() {
  sortColumns.value = []
}

const route = useRoute()
const router = useRouter()

const { search, matchesCardSearch } = useCardSearch()
const toSellFilter = createFilterMode('optcg.cardList.toSellFilterMode')
const onSaleFilter = createFilterMode('optcg.cardList.onSaleFilterMode', ['only', 'hidden', 'visible'])

function getToSellFilterModeFromQuery(query) {
  if (query.to_sell === '1') return 'only'
  return 'visible'
}

function getOnSaleFilterModeFromQuery(query) {
  if (query.on_sale === '1') return 'only'
  if (query.hide_on_sale === '1') return 'hidden'
  return 'visible'
}

const filters = reactive({
  set_type: String(route.query.set_type || ''),
  card_color: String(route.query.card_color || ''),
  card_type: String(route.query.card_type || ''),
  rarity: String(route.query.rarity || ''),
  foil: String(route.query.foil || ''),
  set_group: String(route.query.set_group || ''),
  folder: String(route.query.folder || ''),
  to_sell: route.query.to_sell === '1',
  on_sale: route.query.on_sale === '1',
  hide_on_sale: route.query.hide_on_sale === '1',
  no_folder: route.query.no_folder === '1',
  warehouse: route.query.warehouse === '1',
})

const toSellFilterMode = ref(route.query.to_sell === '1'
  ? getToSellFilterModeFromQuery(route.query)
  : toSellFilter.getStored('visible'))

const onSaleFilterMode = ref(route.query.on_sale === '1' || route.query.hide_on_sale === '1'
  ? getOnSaleFilterModeFromQuery(route.query)
  : onSaleFilter.getStored('visible'))

function applyToSellFilterMode(mode) {
  filters.to_sell = mode === 'only'
}

function cycleToSellFilterMode() {
  if (toSellFilterMode.value === 'visible') toSellFilterMode.value = 'only'
  else toSellFilterMode.value = 'visible'
}

const toSellFilterButtonLabel = computed(() => {
  if (toSellFilterMode.value === 'only') return 'To sell only'
  return 'To sell visible'
})

function applyOnSaleFilterMode(mode) {
  filters.on_sale = mode === 'only'
  filters.hide_on_sale = mode === 'hidden'
}

function cycleOnSaleFilterMode() {
  if (onSaleFilterMode.value === 'visible') onSaleFilterMode.value = 'only'
  else if (onSaleFilterMode.value === 'only') onSaleFilterMode.value = 'hidden'
  else onSaleFilterMode.value = 'visible'
}

const onSaleFilterButtonLabel = computed(() => {
  if (onSaleFilterMode.value === 'only') return 'On sale only'
  if (onSaleFilterMode.value === 'hidden') return 'On sale hidden'
  return 'On sale visible'
})

applyToSellFilterMode(toSellFilterMode.value)
applyOnSaleFilterMode(onSaleFilterMode.value)
search.value = String(route.query.q || '')

function getDistinctValuesByKey(key) {
  const values = new Set()
  for (const card of cards.value) {
    const raw = card?.[key]
    if (raw === undefined || raw === null) continue
    const normalized = String(raw).trim()
    if (!normalized) continue
    values.add(normalized)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const availableRarities = computed(() => getDistinctValuesByKey('rarity'))
const availableGroups = ref([]) // [{ id, name, card_count }] fetched from /api/v1/groups/
const availableFolders = ref([]) // fetched from /api/v1/cards/folders/

function resetFilters() {
  filters.set_type = ''
  filters.card_color = ''
  filters.card_type = ''
  filters.rarity = ''
  filters.foil = ''
  filters.set_group = ''
  filters.folder = ''
  filters.no_folder = false
  filters.warehouse = false
  toSellFilterMode.value = 'visible'
  applyToSellFilterMode(toSellFilterMode.value)
  onSaleFilterMode.value = 'visible'
  applyOnSaleFilterMode(onSaleFilterMode.value)
  search.value = ''
}

function toggleFilter(key, value) {
  filters[key] = filters[key] === value ? '' : value
}

async function toggleFavourite(card) {
  card.favourite = !card.favourite
  await authenticatedApiFetch(`/api/v1/cards/${card.id}/collection/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ favourite: card.favourite })
  })
}

async function updateCardQuantity(card, delta) {
  let action = delta > 0 ? 'increase' : 'decrease';
  const confirmMsg = `Are you sure you want to ${action} the quantity of '${card.card_name}'?`;
  if (!window.confirm(confirmMsg)) return;
  const newQty = Math.max(0, (Number(card.quantity) || 0) + delta)
  card.quantity = newQty
  const groupEntry = card.group_entries?.[0]
  if (groupEntry) {
    await authenticatedApiFetch(`/api/v1/cards/${card.id}/groups/${groupEntry.id}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity: newQty })
    })
  }
}

async function updateSellQuantity(card, delta) {
  const currentQty = card.sell_to_sell ? (Number(card.sell_quantity_to_sell) || 0) : 0

  if (delta < 0 && currentQty <= 1) {
    card.sell_to_sell = false
    card.sell_quantity_to_sell = 0
    await authenticatedApiFetch(`/api/v1/cards/${card.id}/collection/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sell_to_sell: false, sell_quantity_to_sell: 0 })
    })
    return
  }

  const maxQty = Number(card.quantity) || 0
  const newQty = Math.min(maxQty, Math.max(0, currentQty + delta))
  card.sell_to_sell = newQty > 0
  card.sell_quantity_to_sell = newQty
  await authenticatedApiFetch(`/api/v1/cards/${card.id}/collection/`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sell_to_sell: card.sell_to_sell, sell_quantity_to_sell: newQty })
  })
}

async function removeCardOrDecrease(card) {
  const quantity = Number(card.quantity) || 0
  const deleteThreshold = card.to_buy ? 0 : 1

  if (quantity <= deleteThreshold) {
    const confirmMsg = `Are you sure you want to delete '${card.card_name}'?`
    if (!window.confirm(confirmMsg)) return

    await authenticatedApiFetch(`/api/v1/cards/${card.id}/`, {
      method: 'DELETE',
    })

    cards.value = cards.value.filter(item => item.id !== card.id)
    return
  }

  await updateCardQuantity(card, -1)
}





const filteredCards = computed(() => cards.value)

function getApiOrderingFromSort() {
  if (!sortColumns.value.length) return null
  const primarySort = sortColumns.value[0]
  if (primarySort.key !== 'market_price') return null
  return primarySort.dir === -1 ? '-market_price' : 'market_price'
}

const sortedCards = computed(() => {
  const apiOrdering = getApiOrderingFromSort()
  if (apiOrdering) {
    // For market price, sorting is delegated to the API across all filtered rows.
    return filteredCards.value
  }

  const effectiveSortColumns = sortColumns.value.length ? sortColumns.value : defaultSortColumns
  return [...filteredCards.value].sort((a, b) => {
    for (const { key, dir } of effectiveSortColumns) {
      const va = a[key], vb = b[key]
      if (va !== vb) return (va > vb ? 1 : -1) * dir
    }
    return 0
  })
})



function sortBy(key) {
  const idx = sortColumns.value.findIndex(c => c.key === key)
  if (idx === -1) {
    sortColumns.value = [{ key, dir: -1 }]         // 1st click → DESC (single active sort)
  } else if (sortColumns.value[0].dir === -1) {
    sortColumns.value = [{ key, dir: 1 }]          // 2nd click → ASC
  } else {
    sortColumns.value = []                          // 3rd click → disable active sort (fallback keeps card_id order)
  }
}

function sortIcon(key) {
  const col = sortColumns.value.find(c => c.key === key)
  if (!col) return '-'
  const rank = sortColumns.value.indexOf(col) + 1
  const arrow = col.dir === 1 ? '˄' : '˅'
  return sortColumns.value.length > 1 ? `${arrow} ${rank}` : arrow
}




function fetchPageCardsMarketPrices() {
  return fetchMarketPricesForCards(pagedCards.value)
}

function getLoadedCardById(cardId) {
  return cards.value.find(card => card.id === cardId) || null
}

async function fetchCardsForCurrentSelection() {
  const params = buildFilterParams()
  params.set('page_size', '2000')
  const ordering = getApiOrderingFromSort()
  if (ordering) params.set('ordering', ordering)

  const selection = []
  const seen = new Set()
  let currentPage = 1
  let knownTotal = null
  const progressId = showPersistentMessage('Loading current selection... 0', 'info')

  try {
    while (true) {
      params.set('page', String(currentPage))
      const res = await authenticatedApiFetch(`/api/v1/cards/?${params}`)
      if (!res.ok) throw new Error(`Selection load failed: ${res.status}`)

      const data = await res.json()
      const pageItems = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : [])

      if (!pageItems.length) break

      for (const rawCard of pageItems) {
        const normalized = normalizeCard(rawCard)
        if (!normalized?.id || seen.has(normalized.id)) continue
        seen.add(normalized.id)

        // Reuse loaded card objects when available so visible cards update immediately.
        selection.push(getLoadedCardById(normalized.id) || normalized)
      }

      const totalCount = Number(data?.count)
      if (Number.isFinite(totalCount)) knownTotal = totalCount
      updateMessage(
        progressId,
        `Loading current selection... ${selection.length}${knownTotal != null ? ` / ${knownTotal}` : ''}`,
      )

      if (!Number.isFinite(totalCount)) break
      if (selection.length >= totalCount) break
      currentPage += 1
    }
  } finally {
    dismissMessage(progressId)
  }

  return selection
}

async function fetchFilteredCardsMarketPrices() {
  const selection = await fetchCardsForCurrentSelection()
  return fetchMarketPricesForCards(selection)
}


function getEffectiveCardPrice(card) {
  const realPrice = Number(card.real_price)
  if (Number.isFinite(realPrice) && realPrice > 0) return realPrice

  const marketPrice = Number(card.market_price)
  return Number.isFinite(marketPrice) && marketPrice > 0 ? marketPrice : null
}

const pageSizePref = createPageSizePref('optcg.cardList.pageSize')

function pushPageSizePreferenceToDataLayer(size, source = 'change') {
  if (typeof window === 'undefined') return

  const normalizedSize = Number(size) || 0

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'card_list_page_size_preference',
    elements_per_page: normalizedSize,
    page_size: normalizedSize,
    preference_name: 'pageSize',
    preference_value: normalizedSize,
    source,
  })
}

function setPageSize(size, source = 'user') {
  const normalizedSize = Number(size) || 0
  if (normalizedSize <= 0) return

  pageSize.value = normalizedSize
  pageSizePref.persist(normalizedSize)
  pushPageSizePreferenceToDataLayer(normalizedSize, source)
}

async function authenticatedApiFetch(path, options = {}) {
  try {
    const url = path.startsWith('http') ? path : `${BACKEND_ORIGIN}${path}`
    const response = await authStore.authFetch(url, options)

    if (response.status === 401) {
      authStore.logout()
      router.replace({ name: 'login' })
      throw new Error('Session expired')
    }

    return response
  } catch (error) {
    if (error instanceof Error && error.message === 'Not authenticated') {
      router.replace({ name: 'login' })
    }
    throw error
  }
}

const page = ref(Number(route.query.page) || 1)
const serverTotal = ref(0)
const serverStats = ref({ unique_count: 0, total_quantity: 0, total_value: 0, to_sell_count: 0, to_sell_value: 0 })
const globalStats = ref({ unique_count: 0, total_quantity: 0, total_value: 0, to_sell_count: 0, to_sell_value: 0 })

function syncFiltersToUrl() {
  const q = {}
  if (filters.set_type) q.set_type = filters.set_type
  if (filters.card_color) q.card_color = filters.card_color
  if (filters.card_type) q.card_type = filters.card_type
  if (filters.rarity) q.rarity = filters.rarity
  if (filters.foil) q.foil = filters.foil
  if (filters.set_group) q.set_group = filters.set_group
  if (filters.folder) q.folder = filters.folder
  if (filters.to_sell) q.to_sell = '1'
  if (filters.on_sale) q.on_sale = '1'
  if (filters.hide_on_sale) q.hide_on_sale = '1'
  if (filters.no_folder) q.no_folder = '1'
  if (filters.warehouse) q.warehouse = '1'
  if (search.value) q.q = search.value
  if (page.value > 1) q.page = page.value
  router.replace({ query: q })
}
const pageSize = ref(pageSizePref.getStored(30))
const pageCount = computed(() => Math.ceil(serverTotal.value / pageSize.value) || 1)

const totalCardCount = computed(() => globalStats.value.total_quantity)

const totalUniqueCardCount = computed(() => globalStats.value.unique_count)

const filteredCardCount = computed(() => serverStats.value.total_quantity)

const filteredUniqueCardCount = computed(() => serverTotal.value)

const totalCardValue = computed(() => globalStats.value.total_value)

const totalCardValueFormatted = computed(() => `${totalCardValue.value.toFixed(2)}€`)

const filteredCardValue = computed(() => serverStats.value.total_value)

const filteredCardValueFormatted = computed(() => `${filteredCardValue.value.toFixed(2)}€`)

const toSellCardCount = computed(() => globalStats.value.to_sell_count)

const toSellCardValue = computed(() => globalStats.value.to_sell_value)

const toSellCardValueFormatted = computed(() => `${toSellCardValue.value.toFixed(2)}€`)

const filteredToSellCardCount = computed(() => serverStats.value.to_sell_count)

const filteredToSellCardValue = computed(() => serverStats.value.to_sell_value)

const filteredToSellCardValueFormatted = computed(() => `${filteredToSellCardValue.value.toFixed(2)}€`)

const pagedCards = computed(() => sortedCards.value)

function updateAllListings(source = sortedCards.value) {
  source.forEach(card => {
    if (!card.cardtrader_product_id) return

    const price = Number(card.cardtrader_sell_price)
    if (!Number.isFinite(price) || price <= 0) return

    const qty = Number(card.cardtrader_sell_quantity ?? card.sell?.quantity_to_sell ?? 1)
    if (!Number.isInteger(qty) || qty <= 0) return

    updateCardtraderListing(card, price, qty)
  })
}

function applyBulkMultiplier(mult, source = filteredCards.value) {
  if (!mult) return
  const eligible = source.filter(c => c.cardtrader_product_id && c.market_price)
  eligible.forEach(card => {
    const price = Math.round(card.market_price * mult * 100) / 100
    localStorage.setItem(`optcg_mult_${card.id}`, mult)
    card.cardtrader_sell_price = price
    card._multApply = { mult }
  })
}
function prevPage() { if (page.value > 1) page.value-- }
function nextPage() { if (page.value < pageCount.value) page.value++ }
function setPage(p) { if (p >= 1 && p <= pageCount.value) page.value = p }
watch(sortedCards, () => {
  if (page.value > pageCount.value) page.value = pageCount.value || 1
})
watch([filters, search], () => {
  if (page.value === 1) {
    syncFiltersToUrl()
  } else {
    page.value = 1 // triggers watch(page) which calls syncFiltersToUrl
  }
}, { deep: true })
watch(page, () => {
  syncFiltersToUrl()
})

watch(toSellFilterMode, (mode) => {
  applyToSellFilterMode(mode)
  toSellFilter.persist(mode)
})

watch(onSaleFilterMode, (mode) => {
  applyOnSaleFilterMode(mode)
  onSaleFilter.persist(mode)
})

// Sync filters back from URL when navigating externally (e.g. clicking the logo)
watch(() => route.query, (newQuery) => {
  filters.set_type = String(newQuery.set_type || '')
  filters.card_color = String(newQuery.card_color || '')
  filters.card_type = String(newQuery.card_type || '')
  filters.rarity = String(newQuery.rarity || '')
  filters.foil = String(newQuery.foil || '')
  filters.set_group = String(newQuery.set_group || '')
  filters.folder = String(newQuery.folder || '')
  filters.no_folder = newQuery.no_folder === '1'
  filters.warehouse = newQuery.warehouse === '1'
  toSellFilterMode.value = getToSellFilterModeFromQuery(newQuery)
  applyToSellFilterMode(toSellFilterMode.value)
  onSaleFilterMode.value = getOnSaleFilterModeFromQuery(newQuery)
  applyOnSaleFilterMode(onSaleFilterMode.value)
  search.value = String(newQuery.q || '')
}, { deep: true })

watch(pageSize, () => {
  page.value = 1
})

watch(sortColumns, () => {
  sortPref.persist(sortColumns.value)
  if (page.value === 1) {
    syncFiltersToUrl()
  } else {
    page.value = 1
  }
}, { deep: true })

async function loadCards() {
  const params = new URLSearchParams()
  if (search.value) params.set('search', search.value)
  if (filters.set_type) params.set('set_type', filters.set_type)
  if (filters.card_color) params.set('card_color', filters.card_color)
  if (filters.card_type) params.set('card_type', filters.card_type)
  if (filters.rarity) params.set('rarity', filters.rarity)
  if (filters.foil) params.set('foil', filters.foil)
  if (filters.set_group) params.set('group', filters.set_group)
  if (filters.folder) params.set('folder', filters.folder)
  params.set('hide_to_buy', 'true')
  if (filters.to_sell) params.set('to_sell', 'true')
  if (filters.on_sale) params.set('on_sale', 'true')
  if (filters.hide_on_sale) params.set('hide_on_sale', 'true')
  if (filters.no_folder) params.set('no_folder', 'true')
  if (filters.warehouse) params.set('warehouse', 'true')
  const ordering = getApiOrderingFromSort()
  if (ordering) params.set('ordering', ordering)
  params.set('page', page.value)
  params.set('page_size', pageSize.value)

  const res = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/cards/?${params}`)
  if (!res.ok) throw new Error(`Load failed: ${res.status}`)
  const data = await res.json()

  serverTotal.value = data.count ?? 0
  cards.value = (data.results ?? data).map(c => normalizeCard(c))
}

function buildFilterParams() {
  const params = new URLSearchParams()
  if (search.value) params.set('search', search.value)
  if (filters.set_type) params.set('set_type', filters.set_type)
  if (filters.card_color) params.set('card_color', filters.card_color)
  if (filters.card_type) params.set('card_type', filters.card_type)
  if (filters.rarity) params.set('rarity', filters.rarity)
  if (filters.foil) params.set('foil', filters.foil)
  if (filters.set_group) params.set('group', filters.set_group)
  if (filters.folder) params.set('folder', filters.folder)
  params.set('hide_to_buy', 'true')
  if (filters.to_sell) params.set('to_sell', 'true')
  if (filters.on_sale) params.set('on_sale', 'true')
  if (filters.hide_on_sale) params.set('hide_on_sale', 'true')
  if (filters.no_folder) params.set('no_folder', 'true')
  if (filters.warehouse) params.set('warehouse', 'true')
  return params
}

async function loadStats() {
  const res = await authenticatedApiFetch(`/api/v1/cards/stats/?${buildFilterParams()}`)
  if (!res.ok) return
  serverStats.value = await res.json()
}

async function loadGlobalStats() {
  const res = await authenticatedApiFetch('/api/v1/cards/stats/')
  if (!res.ok) return
  globalStats.value = await res.json()
}

onMounted(async () => {
  cardActionsStore.registerFetchPagePrices(fetchPageCardsMarketPrices)
  cardActionsStore.registerFetchFilteredPrices(fetchFilteredCardsMarketPrices)
  cardActionsStore.registerReloadCards(loadCards)
  cardActionsStore.registerFetchPageBlueprints(() => fetchCardtraderDataForCards(pagedCards.value))
  cardActionsStore.registerFetchFilteredBlueprints(async () => {
    const selection = await fetchCardsForCurrentSelection()
    return fetchCardtraderDataForCards(selection)
  })
  cardActionsStore.registerUpdateAllListings(async () => {
    const selection = await fetchCardsForCurrentSelection()
    return updateAllListings(selection)
  })
  cardActionsStore.registerApplyBulkMultiplier(async (mult) => {
    const selection = await fetchCardsForCurrentSelection()
    return applyBulkMultiplier(mult, selection)
  })
  cardActionsStore.registerCheckCtPrices(async () => {
    const selection = await fetchCardsForCurrentSelection()
    await fetchCtMarketPricesForCards(selection)
    await loadCards()
  })
  pageSizePref.persist(pageSize.value)
  pushPageSizePreferenceToDataLayer(pageSize.value, 'init')
  await Promise.all([loadCards(), loadStats(), loadGlobalStats()])
  const [groupsRes, foldersRes] = await Promise.all([
    authenticatedApiFetch('/api/v1/groups/?page_size=500'),
    authenticatedApiFetch('/api/v1/cards/folders/'),
  ])
  if (groupsRes.ok) {
    const data = await groupsRes.json()
    availableGroups.value = (data.results ?? data).sort((a, b) => a.name.localeCompare(b.name))
  }
  if (foldersRes.ok) availableFolders.value = await foldersRes.json()

  let apiReloadTimer = null
  watch(
    () => [
      search.value,
      filters.set_type, filters.card_color, filters.card_type, filters.rarity, filters.foil,
      filters.set_group, filters.folder, filters.no_folder, filters.warehouse,
      filters.to_sell,
      filters.on_sale, filters.hide_on_sale,
      sortColumns.value[0]?.key || '',
      sortColumns.value[0]?.dir || 0,
      page.value, pageSize.value,
    ],
    () => {
      clearTimeout(apiReloadTimer)
      apiReloadTimer = setTimeout(() => { loadCards(); loadStats() }, 300)
    },
  )
})

</script>

<template>
    <Container fw>
      <Container>
        <Boxes spaceTopBot fh class="align-content-start">
          <Box size="100">
            <Space height="3"/>
            <h1 class="light m-0">Cards to sell</h1>
          </Box>
          <Box size="100">
            <Pagination v-model:currentPage="page" :totalPages="pageCount" />
            <div class="">
              <Button
                  v-for="n in [9, 30, 60, 120, 300, serverTotal]"
                  :key="n"
                  size="S"
                  :class="{ active: pageSize === n }"
                  invert
                @click="setPageSize(n, 'user')"
              >
                  {{ n === serverTotal ? 'All' : n }}
              </Button>
              <span>&nbsp;<span class="light-grey-text">|</span>&nbsp;&nbsp;&nbsp;</span>
              <Button size="S" color="light-grey" class="transp-bg" invert @click="toggleAllDetails">
                {{ allDetailsExpanded ? 'Collapse all' : 'Expand all' }}
              </Button>
            </div>
          </Box>
          <Box size="20" padding="0">
            <Boxes>
              <Box size="100">
                <input
                  v-model="search"
                  type="text"
                  placeholder="Search by ID, name, set, ..."
                  class="search-input"
                  style="margin-bottom: 0.5em; width: 250px;"
                />
                <p class="m-0">
                <template v-if="availableGroups.length">
                  <Button size="XS" invert:class="{ active: filters.set_group === '' }" @click="filters.set_group = ''">All</Button>
                  <Button
                    v-for="group in availableGroups"
                    :key="`filter-set-group-${group.id}`"
                    size="XS"
                    :class="{ active: filters.set_group === group.name }"
                    invert
                    @click="toggleFilter('set_group', group.name)"
                  >
                    {{ group.name }}&nbsp;<span style="opacity:0.7">({{ group.card_count }})</span>
                  </Button>
                </template>
                </p>
                <p class="m-0" v-if="availableFolders.length">
                  <Button size="XS" invert :class="{ active: filters.folder === '' && !filters.no_folder && !filters.warehouse }" @click="filters.folder = ''; filters.no_folder = false; filters.warehouse = false">All</Button>
                  <Button
                    v-for="value in availableFolders"
                    :key="`filter-folder-${value}`"
                    size="XS"
                    :class="{ active: filters.folder === value }"
                    invert
                    @click="toggleFilter('folder', value); filters.no_folder = false; filters.warehouse = false"
                  >{{ value }}</Button>
                  <Button size="XS" invert :class="{ active: filters.no_folder }" @click="filters.no_folder = !filters.no_folder; filters.folder = ''; filters.warehouse = false">No folder</Button>
                  <Button size="XS" :class="{ 'blue-btn': filters.warehouse, 'blue-btn-invert': !filters.warehouse }" @click="filters.warehouse = !filters.warehouse; filters.folder = ''; filters.no_folder = false">CT Warehouse</Button>
                </p>
                <p class="m-0">
                  <Button size="XS" :class="{ 'color-btn-invert': toSellFilterMode === 'visible', 'color-btn': toSellFilterMode === 'only' }" @click="cycleToSellFilterMode">{{ toSellFilterButtonLabel }}</Button>
                  <Button size="XS" :class="{ 'blue-btn-invert': onSaleFilterMode === 'visible', 'blue-btn': onSaleFilterMode === 'only', 'grey-btn': onSaleFilterMode === 'hidden' }" @click="cycleOnSaleFilterMode">{{ onSaleFilterButtonLabel }}</Button>
                </p>
                <p class="mt-0">
                  <Button size="XS" color="light-grey" @click="resetFilters" invert class="transp-bg">Reset filters</Button>
                </p>
                <Sep class="my-1"/>
                <p class="m-0">
                  <Button @click="sortBy('market_price')" size="XS" color="color">Market Price&nbsp;&nbsp;{{ sortIcon('market_price') }}</Button>
                </p>
                <p class="mt-0">
                  <Button @click="resetSorting" size="XS" color="light-grey" invert class="transp-bg">Reset sorting</Button>
                </p>
              </Box>
              <Sep/>
              <Box size="100">
              </Box>
            </Boxes>
          </Box>
          <Box size="80" padding="0">
            <Boxes class="align-items-start">
              <CardBox
                v-for="card in pagedCards"
                :key="card.id"
                :card="card"
                :expanded="expandedCards.has(card.id)"
                @toggle="toggleDetails"
                @remove="removeCardOrDecrease"
                @edit="card => openModal('edit', card)"
                @increment="card => updateCardQuantity(card, 1)"
                @increment-sell="card => updateSellQuantity(card, 1)"
                @remove-sell="card => updateSellQuantity(card, -1)"
                @fetch-card="card => fetchMarketPricesForCards([card], true)"
                @fetch-cardtrader="fetchCardtraderDataForCard"
                @refetch-cardtrader="card => refetchExpansionForCard(card).then(() => {}).catch(() => {})"
                @sell-on-cardtrader="createCardtraderProduct"
                @check-cardtrader="checkCardtraderListing"
                @update-cardtrader-listing="(card, price, qty) => updateCardtraderListing(card, price, qty)"
                @delete-cardtrader="deleteCardtraderListing"
                @toggle-favourite="toggleFavourite"
                @save-ct-price="saveCtPrice"
                @save-real-price="saveRealPrice"
              />
            </Boxes>
          </Box>
          <Box size="100" class="flex align-items-center">
            <Pagination v-model:currentPage="page" :totalPages="pageCount" />
          </Box>
          <Box size="100">
            <p class="m-0 L"><b>Cards:</b> <span v-if="filteredCardCount !== totalCardCount" class=""> {{ filteredCardCount }}</span><span v-if="filteredCardCount !== totalCardCount" class="light-grey-text">/</span><span :class="{'light-grey-text': filteredCardCount !== totalCardCount}">{{ totalCardCount }}</span> <span class="light-grey-text S">({{ filteredUniqueCardCount !== totalUniqueCardCount ? filteredUniqueCardCount + '/' : '' }}{{ totalUniqueCardCount }} unique)</span></p>
            <p class="m-0"><b>Value:</b> <span v-if="filteredCardValue !== totalCardValue" class="">{{ filteredCardValueFormatted }}</span><span v-if="filteredCardValue !== totalCardValue" class="light-grey-text">/</span><span :class="{'light-grey-text': filteredCardValue !== totalCardValue}">{{ totalCardValueFormatted }}</span></p>
          </Box>
        </Boxes>
      </Container>

      <CardEditModal
        :visible="activeModal === 'edit'"
        :card="modalCard"
        @close="closeModal()"
        @saved="loadCards"
        @deleted="id => { cards = cards.filter(c => c.id !== id); closeModal() }"
      />
    </Container>
</template>